import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import { StudentEnrolInterface } from "@/ts/interfaces/Enrol/StudentEntrolInterface";
import { SubjectPerformanceInterface } from "@/ts/interfaces/Enrol/SubjectPerformanceInterface";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { createStudentEnrolmentThunk } from "../enrolmentThunks";

interface StudentEnrolState {
    enrolStudent: StudentEnrolInterface;

    createStudentEnrolRequestStatus: REQUEST_STATUS;
    createStudentEnrolRequestError: string | null;
}

const mathematicsSubjectPerformance = {
    id: 1,
    subjectName: "Mathematics",
    level: 1 as 0 | 1 | 2,
    isEnabled: true,
    subFields: null
};

const englishSubjectPerformance = {
    id: 2,
    subjectName: "English",
    level: 1 as 0 | 1 | 2,
    isEnabled: true,
    subFields: null
};

const artsSubjectPerformance = {
    id: 3,
    subjectName: "The arts",
    level: 1 as 0 | 1 | 2,
    isEnabled: true,
    subFields: [
        {
            id: 1,
            subFieldName: "Drama"
        },
        {
            id: 2,
            subFieldName: "Music"
        },
        {
            id: 3,
            subFieldName: "Visual arts"
        }
    ]
};

const humanitiesSubjectPerformance = {
    id: 4,
    subjectName: "Humanities",
    level: 1 as 0 | 1 | 2,
    isEnabled: true,
    subFields: [
        {
            id: 1,
            subFieldName: "Geography"
        },
        {
            id: 2,
            subFieldName: "History"
        },
        {
            id: 3,
            subFieldName: "Civics"
        },
        {
            id: 4,
            subFieldName: "Economics"
        }
    ]
};

const pdhpeSubjectPerformance = {
    id: 5,
    subjectName: "PDHPE",
    level: 1 as 0 | 1 | 2,
    isEnabled: true,
    subFields: [
        {
            id: 1,
            subFieldName: "Health"
        },
        {
            id: 2,
            subFieldName: "Physical education"
        }
    ]
};

const cloneSubjectPerformance = (
    subjectPerformance: SubjectPerformanceInterface
): SubjectPerformanceInterface => ({
    ...subjectPerformance,
    subFields:
        subjectPerformance.subFields?.map(subField => ({ ...subField })) ?? null
});

export const standardSubjectPerformances: SubjectPerformanceInterface[] = [
    mathematicsSubjectPerformance,
    englishSubjectPerformance,
    artsSubjectPerformance,
    humanitiesSubjectPerformance,
    pdhpeSubjectPerformance
];

export const oexGradeNineTenSubjectPerformances: SubjectPerformanceInterface[] =
    [
        mathematicsSubjectPerformance,
        englishSubjectPerformance,
        humanitiesSubjectPerformance,
        pdhpeSubjectPerformance
    ];

export const getDefaultSubjectPerformances = (
    useOexGradeNineTenSubjectList = false
) => {
    const subjectPerformances = useOexGradeNineTenSubjectList
        ? oexGradeNineTenSubjectPerformances
        : standardSubjectPerformances;

    return subjectPerformances.map(cloneSubjectPerformance);
};

const initialState: StudentEnrolState = {
    enrolStudent: {
        firstName: "",
        lastName: "",
        startingTerm: "",
        program: null,
        subjectPerformances: getDefaultSubjectPerformances(),
        electives: []
    },

    createStudentEnrolRequestStatus: REQUEST_STATUS.IDLE,
    createStudentEnrolRequestError: null
};

const enrolStudentSlice = createSlice({
    name: "enrolStudent",
    initialState,
    reducers: {
        setEnrolStudent(state, action: PayloadAction<StudentEnrolInterface>) {
            state.enrolStudent = action.payload;
        },
        resetEnrolStudentState() {
            return initialState;
        }
    },
    extraReducers: builder => {
        builder
            // Create student enrolment
            .addCase(createStudentEnrolmentThunk.pending, state => {
                state.createStudentEnrolRequestStatus = REQUEST_STATUS.PENDING;
                state.createStudentEnrolRequestError = null;
            })
            .addCase(createStudentEnrolmentThunk.fulfilled, state => {
                state.createStudentEnrolRequestStatus = REQUEST_STATUS.SUCCESS;
            })
            .addCase(createStudentEnrolmentThunk.rejected, (state, action) => {
                state.createStudentEnrolRequestStatus = REQUEST_STATUS.ERROR;
                state.createStudentEnrolRequestError = action.payload as string;
            });
    }
});

export const { setEnrolStudent, resetEnrolStudentState } =
    enrolStudentSlice.actions;

export default enrolStudentSlice.reducer;
